import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import Input from '../../components/UI/Input';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import { VerifyEmailData } from '../../types';

const verifyEmailSchema = z.object({
  code: z.number().int().positive('Please enter a valid verification code'),
});

const VerifyEmail: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);
  const { verifyEmail, generateEmailVerificationCode, user } = useAuth();
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors } } = useForm<VerifyEmailData>({
    resolver: zodResolver(verifyEmailSchema),
  });

  const onSubmit = async (data: VerifyEmailData) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const success = await verifyEmail(data.code);
      
      if (success) {
        setSuccess('Email verification successful! Redirecting.');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError('Invalid verification code. Please try again.');
      }
    } catch (err: unknown) {
      // Handle specific API error messages
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An error occurred. Please try again.');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setResendSuccess(null);
    setError(null);
    
    try {
      const success = await generateEmailVerificationCode();
      
      if (success) {
        setResendSuccess('A new verification code has been sent to your email.');
      } else {
        setError('Failed to send verification code. Please try again.');
      }
    } catch (err: unknown) {
      // Handle specific API error messages
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An error occurred. Please try again.');
      }
      console.error(err);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-[calc(100dvh-4.5rem)] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <h2 className="text-3xl font-extrabold text-gray-900">Verify Your Email</h2>
          <p className="mt-2 text-gray-600">
            We've sent a verification code to {user?.email}.<br />
            Please enter the code below to verify your email address.
          </p>
        </motion.div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="py-8 px-4 sm:px-10">
            {error && (
              <div className="mb-4 p-3 bg-error-50 text-error-600 text-sm rounded-md border border-error-200">
                {error}
              </div>
            )}
            
            {success && (
              <div className="mb-4 p-3 bg-success-50 text-success-600 text-sm rounded-md border border-success-200">
                {success}
              </div>
            )}
            
            {resendSuccess && (
              <div className="mb-4 p-3 bg-success-50 text-success-600 text-sm rounded-md border border-success-200">
                {resendSuccess}
              </div>
            )}
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <Input
                id="code"
                type="number"
                label="Verification Code"
                error={errors.code?.message}
                {...register('code', { valueAsNumber: true })}
              />

              <div>
                <Button
                  type="submit"
                  fullWidth
                  isLoading={isLoading}
                >
                  Verify Email
                </Button>
              </div>
              
              <div className="text-center mt-4">
                <p className="text-sm text-gray-600">
                  Didn't receive the code?{' '}
                  <button
                    type="button"
                    className="font-medium text-primary-600 hover:text-primary-500"
                    onClick={handleResendCode}
                    disabled={isResending}
                  >
                    {isResending ? 'Sending...' : 'Resend Code'}
                  </button>
                </p>
              </div>
            </form>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default VerifyEmail;